class FairfCampaign extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.slides = [...this.querySelectorAll('.fairf-campaign__image')];
    this.slides.forEach(slide => { slide.dataset.defaultAlt = slide.alt; });
    this.index = 0;
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.paused = this.motion.matches;
    this.visible = true;
    this.abort = new AbortController();
    const listen = (node, event, callback) => node.addEventListener(event, callback, { signal: this.abort.signal });
    this.hero = this.querySelector('.fairf-campaign');
    this.header = document.querySelector('.section-header');
    this.cutout = document.querySelector('[data-fairf-logo-cutout]');
    this.cutoutFrames = this.cutout ? [0, 1].map(() => {
      const image = document.createElement('img');
      image.alt = '';
      image.className = 'fairf-logo-cutout__image';
      this.cutout.append(image);
      return image;
    }) : [];
    this.cutoutIndex = 0;
    listen(window, 'resize', () => this.fitHero());
    this.slides.forEach(slide => listen(slide, 'load', () => {
      this.fitPhoto(slide);
      if (slide === this.slides[this.index]) this.paintCutout(slide);
    }));
    listen(this, 'mouseenter', () => { this.hovered = true; this.schedule(); });
    listen(this, 'mouseleave', () => { this.hovered = false; this.schedule(); });
    listen(this, 'focusin', () => { this.paused = true; this.schedule(); });
    listen(document, 'visibilitychange', () => this.schedule());
    listen(this.motion, 'change', () => { this.paused = this.motion.matches; this.schedule(); });
    this.observer = new IntersectionObserver(entries => { this.visible = entries[0].isIntersecting; this.schedule(); }, { threshold: .2 });
    this.observer.observe(this);
    if (this.cutout) {
      this.logoObserver = new IntersectionObserver(entries => {
        this.cutout.parentElement.classList.toggle('is-over-hero', entries[0].isIntersecting);
      });
      this.logoObserver.observe(this.hero);
    }
    this.headerSections = [...document.querySelectorAll('.shopify-section-group-header-group')];
    this.promises = this.querySelector('.fairf-colour-strip');
    this.layoutObserver = new ResizeObserver(() => this.fitHero());
    [...this.headerSections, this.promises].filter(Boolean).forEach(element => this.layoutObserver.observe(element));
    this.fitHero();
    this.palette();
    this.load(0).then(loaded => { if (loaded && this.isConnected) this.paintCutout(this.slides[this.index]); });
    this.load(1);
    this.schedule();
  }
  load(index) {
    const slide = this.slides[index % this.slides.length];
    slide.parentElement.querySelectorAll('source[data-srcset]').forEach(source => {
      source.srcset = source.dataset.srcset;
      delete source.dataset.srcset;
    });
    if (!slide.getAttribute('src')) {
      if (slide.dataset.srcset) slide.srcset = slide.dataset.srcset;
      slide.src = slide.dataset.src;
    }
    return slide.decode().then(() => true).catch(() => false);
  }
  fitHero() {
    this.style.setProperty('--fairf-above-hero', `${this.headerSections.reduce((height, section) => height + section.offsetHeight, 0)}px`);
    this.style.setProperty('--fairf-promises-height', `${this.promises?.offsetHeight || 0}px`);
    this.slides.forEach(slide => this.fitPhoto(slide));
    this.paintCutout(this.slides[this.index]);
    if (!this.cutout || !this.header) return;
    const header = this.header.getBoundingClientRect();
    const hero = this.hero.getBoundingClientRect();
    const logo = this.cutout.getBoundingClientRect();
    // Use the hero's photo scale for the logo window without changing the hero crop.
    this.cutout.style.setProperty('--cutout-width', `${hero.width}px`);
    this.cutout.style.setProperty('--cutout-height', `${hero.height}px`);
    this.cutout.style.setProperty('--cutout-x', `${hero.left - logo.left}px`);
    this.cutout.style.setProperty('--cutout-y', `${header.top - logo.top}px`);
  }
  fitPhoto(slide) {
    // Anchor the subject's upper edge, rather than the centre of the photograph.
    // The alternate yellow scene has a wider camera framing for broad displays.
    const width = Number(slide.getAttribute('width'));
    const height = Number(slide.getAttribute('height'));
    const box = this.hero.getBoundingClientRect();
    const renderedHeight = height * Math.max(box.width / width, box.height / height);
    const overflow = renderedHeight - box.height;
    const wide = matchMedia('(min-width: 1800px)').matches;
    if (slide.dataset.wideAlt) slide.alt = wide ? slide.dataset.wideAlt : slide.dataset.defaultAlt;
    const top = Number((wide && slide.dataset.wideSubjectTop) || slide.dataset.subjectTop || 0);
    const position = overflow > 0 ? Math.max(0, Math.min(1, (top * renderedHeight - box.height * .10) / overflow)) * 100 : 0;
    slide.style.objectPosition = `${slide.dataset.subjectX || 50}% ${position.toFixed(3)}%`;
  }
  paintCutout(slide) {
    if (!this.cutout || !slide.complete || !slide.naturalWidth) return;
    const source = slide.currentSrc || slide.src;
    if (source === this.cutoutSource) {
      this.cutoutFrames[this.cutoutIndex].style.objectPosition = getComputedStyle(slide).objectPosition;
      return;
    }
    const firstImage = !this.cutoutSource;
    this.cutoutSource = source;
    this.cutout.style.setProperty('--cutout-shade', ['#eee1bf', '#f9e171'].includes(slide.dataset.accent) ? '.3' : '0');
    this.cutoutIndex = (this.cutoutIndex + 1) % this.cutoutFrames.length;
    const frame = this.cutoutFrames[this.cutoutIndex];
    frame.src = source;
    frame.style.objectPosition = getComputedStyle(slide).objectPosition;
    this.cutoutFrames.forEach((image, index) => {
      image.style.transition = firstImage ? 'none' : '';
      image.classList.toggle('is-active', index === this.cutoutIndex);
    });
    this.cutout.parentElement.classList.add('is-ready');
  }
  palette() {
    // Keep the approved Fairf brand palette stable while the photography changes.
  }
  async show() {
    const next = (this.index + 1) % this.slides.length;
    if (await this.load(next) && this.isConnected && !this.paused && !this.hovered && this.visible && !document.hidden) {
      this.index = next;
      this.slides.forEach((slide, i) => { slide.classList.toggle('is-active', i === next); slide.setAttribute('aria-hidden', String(i !== next)); });
      this.palette();
      this.paintCutout(this.slides[next]);
      this.load(next + 1);
    }
    this.schedule();
  }
  schedule() {
    clearTimeout(this.timer);
    if (this.isConnected && !this.paused && !this.hovered && this.visible && !document.hidden && this.slides.length > 1) this.timer = setTimeout(() => this.show(), Number(this.dataset.interval) || 9000);
  }
  disconnectedCallback() {
    clearTimeout(this.timer);
    this.abort?.abort();
    this.observer?.disconnect();
    this.logoObserver?.disconnect();
    this.layoutObserver?.disconnect();
    this.cutoutFrames?.forEach(image => image.remove());
    this.cutout?.parentElement.classList.remove('is-ready', 'is-over-hero');
    this.cutoutSource = null;
    ['lime', 'green', 'purple'].forEach(name => document.body.style.removeProperty(`--fairf-${name}`));
    this.ready = false;
  }
}
if (!customElements.get('fairf-campaign')) customElements.define('fairf-campaign', FairfCampaign);
